import React from 'react';
import PopupDialog from '../PopupDialog';
import PrintBOV from '../PrintBOV';
import {getTemplate, bovOfficeFor} from '../PrintBOV/bov-templates';
import {fetchData, mutateData} from '../Service/graphQlService';
import {GET_UNDERWRITING_MULTI_TENANT_WITH_PROPERTYID} from '../Graphql/Queries/propertiesQuery';
import {isSeniorAgent} from '../../utils';
import {GET_UNDERWRITING_MULTI_FAMILY, SAVE_UNDERWRITING_MULTI_FAMILY} from './queries';
import calculate, {toNumber} from './model/calculate';
import {emptyUnitMixRow, emptyOtherIncomeRow} from './model/fields';
import {fromApi, fromMultiTenant, toApi, toProperty, propertyChanged} from './model/mapping';
import Marketing from './tabs/Marketing';
import PropertyInformation from './tabs/PropertyInformation';
import Pricing from './tabs/Pricing';
import CurrentRentRoll from './tabs/CurrentRentRoll';
import OtherIncome from './tabs/OtherIncome';
import Expenses from './tabs/Expenses';
import ProformaRentRoll from './tabs/ProformaRentRoll';

/** Tabs in workbook-sheet order. */
const TABS = [
    {key: 'marketing', name: 'Marketing', Component: Marketing},
    {key: 'property-information', name: 'Property Information', Component: PropertyInformation},
    {key: 'pricing', name: 'Pricing', Component: Pricing},
    {key: 'current-rent-roll', name: 'Current Rent Roll', Component: CurrentRentRoll},
    {key: 'current-reimbursements', name: 'Current Reimbursements', Component: OtherIncome, props: {column: 'current'}},
    {key: 'expenses', name: 'Expenses', Component: Expenses},
    {key: 'proforma-rent-roll', name: 'Proforma Rent Roll', Component: ProformaRentRoll},
    {key: 'proforma-reimbursements', name: 'Proforma Reimbursements', Component: OtherIncome, props: {column: 'proforma'}},
];

const NEW_ROW = {
    unit_mix: () => emptyUnitMixRow(),
    current_other_income: () => emptyOtherIncomeRow(),
    proforma_other_income: () => emptyOtherIncomeRow(),
};

/** Listing statuses where a price change needs the Rep Agreement prompt (as on multi-tenant). */
const CONFIRM_STATUSES = ['Active', 'In Negotiation', 'Under Contract'];

/**
 * The Multi-Family underwriting popup (MF_BOV_Underwriting_Template.xlsx), opened by
 * Financials for properties in the `apartments` category.
 *
 * State is just the workbook's manual inputs; calculate() derives everything else on each
 * render. Saving writes the inputs to underwriting_multi_family and hands the property-record
 * fields back to the parent, which saves the property.
 */
export default class UnderwritingMultiFamily extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            inputs: null,
            // JSON of the last saved mutation input; null until the row exists in the database.
            savedSnapshot: null,
            selectedTab: TABS[0].key,
            isBusy: false,
            isDialogOpen: false,
            dialogTitle: '',
        };
        this.setField = this.setField.bind(this);
        this.setRow = this.setRow.bind(this);
        this.addRow = this.addRow.bind(this);
        this.removeRow = this.removeRow.bind(this);
        this.beforeSubmit = this.beforeSubmit.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
        this.onCancel = this.onCancel.bind(this);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    get propertyId() {
        return this.props.data.arcimedes_row_id;
    }

    /**
     * Loads the saved underwriting, or - the first time a property opens here - starts from
     * what its multi-tenant underwriting already holds.
     */
    async load() {
        const property = this.props.data;
        try {
            if (!this.propertyId) {
                this.setLoaded(fromMultiTenant(null, property), null);
                return;
            }
            const res = await fetchData(GET_UNDERWRITING_MULTI_FAMILY, {propertyId: this.propertyId});
            const row = res && res.data && res.data.getUnderwritingMultiFamily;
            if (row) {
                const inputs = fromApi(row, property);
                this.setLoaded(inputs, JSON.stringify(toApi(inputs, this.propertyId)));
                return;
            }
            const mtRes = await fetchData(GET_UNDERWRITING_MULTI_TENANT_WITH_PROPERTYID, {propertyId: this.propertyId});
            const mtRows = mtRes && mtRes.data && mtRes.data.getUnderwritingMultiTenent;
            this.setLoaded(fromMultiTenant(mtRows && mtRows[0], property), null);
        } catch (error) {
            this.setLoaded(fromMultiTenant(null, property), null);
            this.props.parentSetData({error: `Could not load the underwriting: ${error.message}`});
        }
    }

    setLoaded(inputs, savedSnapshot) {
        if (this.unmounted) return;
        this.setState({inputs, savedSnapshot});
    }

    // ---------------------------------------------------------------- editing

    setField(key, value) {
        this.setState(({inputs}) => ({inputs: {...inputs, [key]: value}}));
    }

    setRow(listKey, index, field, value) {
        this.setState(({inputs}) => {
            const list = inputs[listKey].slice();
            list[index] = {...list[index], [field]: value};
            return {inputs: {...inputs, [listKey]: list}};
        });
    }

    addRow(listKey) {
        this.setState(({inputs}) => ({inputs: {...inputs, [listKey]: inputs[listKey].concat(NEW_ROW[listKey]())}}));
    }

    removeRow(listKey, index) {
        this.setState(({inputs}) => ({
            inputs: {...inputs, [listKey]: inputs[listKey].filter((_, i) => i !== index)},
        }));
    }

    // ----------------------------------------------------------------- saving

    beforeSubmit() {
        if (this.state.isBusy || !this.state.inputs) return;
        this.setState({isBusy: true});

        const {status, source} = this.props.data;
        if (!(CONFIRM_STATUSES.includes(status) && source === 'STRIVE')) {
            this.onSubmit();
            return;
        }
        // Saving writes the valuation price to the listing, so compare against the listing.
        const price = Math.round(calculate(this.state.inputs).valuation.current.price);
        const listPrice = Math.round(toNumber(this.props.data.list_price));
        this.setState({
            isDialogOpen: true,
            dialogTitle: price > 0 && price !== listPrice
                ? 'Do you have an Amendment to the Rep Agreement to change the price?'
                : 'Are you sure you want to save this financial data?',
        });
    }

    onConfirm() {
        this.setState({isDialogOpen: false});
        this.onSubmit();
    }

    onCancel() {
        this.setState({isDialogOpen: false, isBusy: false});
    }

    async onSubmit() {
        const {inputs, savedSnapshot} = this.state;
        const property = this.props.data;
        if (!this.propertyId) {
            this.finishSave({error: 'Save the property before saving its financials.'});
            return;
        }

        const payload = toApi(inputs, this.propertyId);
        const nextProperty = toProperty(property, inputs, calculate(inputs));
        const underwritingChanged = JSON.stringify(payload) !== savedSnapshot;
        const recordChanged = propertyChanged(property, nextProperty);

        if (!underwritingChanged && !recordChanged) {
            this.finishSave({success: 'No data to update'});
            return;
        }

        try {
            if (underwritingChanged) {
                const res = await mutateData(SAVE_UNDERWRITING_MULTI_FAMILY, {input: payload});
                const result = res && res.data && res.data.createOrUpdateUnderwritingMultiFamily;
                if (!result || !result.success) {
                    this.finishSave({error: (result && (result.error || result.message)) || 'Unidentified Error'});
                    return;
                }
                const saved = fromApi(result.underwritingMultiFamily, nextProperty);
                if (!this.unmounted) {
                    this.setState({inputs: saved, savedSnapshot: JSON.stringify(toApi(saved, this.propertyId))});
                }
            }
            this.finishSave(recordChanged ? {success: 'Saved', newData: nextProperty} : {success: 'Saved'});
        } catch (error) {
            this.finishSave({error: error.message || 'Unidentified Error'});
        }
    }

    finishSave(result) {
        if (!this.unmounted) this.setState({isBusy: false});
        this.props.parentSetData(result);
    }

    // -------------------------------------------------------------- rendering

    /** Same rule as the multi-tenant popup. */
    isSaveDisabled() {
        const {data, isSuper, agentIsCurrentUser} = this.props;
        const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
        const seniorAgentCanSave = (isSeniorAgent(userData) && data.source == 'External')
            || (data.source == 'STRIVE' && ['Proposal', 'Property Info'].includes(data.status));
        return !seniorAgentCanSave
            && !(isSuper || (agentIsCurrentUser && ['Proposal', 'Property Info', 'Off Market', 'Removed'].includes(data.status)));
    }

    renderPrint(inputs, calc) {
        const data = this.props.data;
        const template = getTemplate(bovOfficeFor({zip: inputs.zip || data.zip, market: data.market}), 'apartments', 'Multi Tenant');
        if (!template) return null;
        return (
            <PrintBOV
                template={template}
                multiFamily={{inputs, calc}}
                proposalData={this.props.proposalData}
                agents={this.props.agents}
                propertyRecord={data}
                propertyMapProps={{
                    latitude: data.latitude ? parseFloat(data.latitude) : 0,
                    longitude: data.longitude ? parseFloat(data.longitude) : 0,
                    name: inputs.name,
                }}
                propertyImages={[1, 2, 3, 4, 5].map(n => data[`website_photo_${n}`])}
            />
        );
    }

    render() {
        const {inputs, selectedTab, isBusy, isDialogOpen, dialogTitle} = this.state;
        const disabled = this.isSaveDisabled();
        const loaded = !!inputs;
        const calc = loaded ? calculate(inputs) : null;
        const tab = TABS.find(t => t.key === selectedTab);
        const TabComponent = tab.Component;

        return (
            <div className='UnderwritingMultiFamily'>
                <PopupDialog title={dialogTitle} isOpen={isDialogOpen} onConfirm={this.onConfirm} onCancel={this.onCancel}/>
                {loaded ? this.renderPrint(inputs, calc) : null}
                <div className='Financials_content multi_tenant_content'>
                    <button
                        type='button'
                        className={`Financials_submit_button${disabled || isBusy ? '_disabled' : ''}`}
                        onClick={this.beforeSubmit}
                        disabled={disabled || isBusy || !loaded}
                    >
                        {isBusy ? 'Saving…' : 'Submit'}
                    </button>
                    {disabled ? <div className='Financials_submit_message'>Don't have permission to save.</div> : null}
                    <div className='multi_tenant_tabs'>
                        {TABS.map(t => (
                            <div
                                key={t.key}
                                className={`tab${selectedTab === t.key ? ' selected' : ''}${loaded ? '' : ' disabled'}`}
                                onClick={() => this.setState({selectedTab: t.key})}
                            >
                                {t.name}
                            </div>
                        ))}
                    </div>
                    <div className='content'>
                        {loaded ?
                            <TabComponent
                                {...(tab.props || {})}
                                inputs={inputs}
                                calc={calc}
                                setField={this.setField}
                                setRow={this.setRow}
                                addRow={this.addRow}
                                removeRow={this.removeRow}
                            />
                            : <div className='mf-loading'>Loading…</div>}
                    </div>
                </div>
            </div>
        );
    }
}
